import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Globe, Link2, RotateCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { PrimaryButton } from "@/components/flow/Primitives";
import { DEFAULT_BRAND } from "@/pages/home/HeroStage";
import { onColor } from "@/lib/brandColor";
import { publicApi, errMsg, appOrigin, BACKEND_URL } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { EASE } from "@/lib/motion";
import { t } from "@/lib/i18n";
import { cn } from "@/lib/utils";

/* ---------------------------------------------------------------------------
   Brand preview — "enter your website, see Listed in your brand".
   Context holds the visitor's brand for the whole landing page (hero loop,
   the owner-journey story, and the white-label section). The form owns the
   states: idle → reading → ready | partial | error — one row, one quiet line beneath.
--------------------------------------------------------------------------- */

const STORAGE_KEY = "listed_brand_preview";
const BrandPreviewCtx = createContext({ brand: null, preview: null, setPreview: () => {}, reset: () => {}, loading: "", setLoading: () => {} });
export const useBrandPreview = () => useContext(BrandPreviewCtx);

/** API shape → the shape HeroStage wants. */
export function toBrand(p) {
  if (!p) return null;
  const domain = p.domain || DEFAULT_BRAND.domain;
  return {
    name: p.name || DEFAULT_BRAND.name,
    color: p.color || DEFAULT_BRAND.color,
    domain,
    ownerDomain: `owners.${domain}`,
    logoUrl: p.logo_url || null,
    // Their own hero photograph (a cached rendition served by Listed) — the mockup's website shows *their* site.
    heroImageUrl: p.hero_image_url ? `${BACKEND_URL}${p.hero_image_url}` : null,
    token: p.token,
  };
}

export function BrandPreviewProvider({ children, initial = null }) {
  const [preview, setPreviewState] = useState(() => {
    if (initial) return initial;
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const setPreview = useCallback((p) => {
    setPreviewState(p);
    try {
      if (p) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(p));
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* private mode */
    }
  }, []);
  const reset = useCallback(() => setPreview(null), [setPreview]);
  // The URL currently being read ("" when idle) — the hero shows it while the phone keeps playing.
  const [loading, setLoading] = useState("");
  const value = useMemo(() => ({ brand: toBrand(preview), preview, setPreview, reset, loading, setLoading }), [preview, setPreview, reset, loading]);
  return <BrandPreviewCtx.Provider value={value}>{children}</BrandPreviewCtx.Provider>;
}

/* ------------------------------------------------------------------ form */
const STEPS = ["l12.bp.step1", "l12.bp.step2", "l12.bp.step3", "l12.bp.step4"];

export function BrandPreviewForm({ className, compact = false }) {
  const { preview, setPreview, reset, setLoading } = useBrandPreview();
  const [url, setUrl] = useState("");
  const [status, setStatus] = useState("idle"); // idle · loading · error
  const [error, setError] = useState("");
  const [step, setStep] = useState(0);
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const ready = !!preview && !editing && status !== "loading";

  // Rotate the "reading your site…" messages while we wait.
  useEffect(() => {
    if (status !== "loading") return undefined;
    setStep(0);
    const id = setInterval(() => setStep((s) => Math.min(STEPS.length - 1, s + 1)), 900);
    return () => clearInterval(id);
  }, [status]);
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const raw = url.trim();
    if (!raw) {
      inputRef.current?.focus();
      return;
    }
    setStatus("loading");
    setLoading(raw.replace(/^https?:\/\//i, "").replace(/\/.*$/, ""));
    setError("");
    try {
      const r = await publicApi.post("/public/brand-preview", { url: raw });
      setPreview(r.data);
      setStatus("idle");
      setEditing(false);
    } catch (err) {
      setStatus("error");
      setError(errMsg(err, t("l12.bp.err_generic")));
    } finally {
      setLoading("");
    }
  };

  const shareLink = preview?.token ? `${appOrigin()}/preview/${preview.token}` : "";
  const partialNote = preview?.partial ? (preview.logo_url ? t("l12.bp.partial_color_short") : t("l12.bp.partial_logo_short")) : "";
  const textBtn = "inline-flex items-center gap-1.5 h-7 rounded-full px-2 -mx-1 text-ink/55 hover:text-ink hover:bg-ink/[0.05] transition-[color,background-color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/20";

  return (
    <div className={cn("min-w-0", className)} data-testid="brand-preview-form" data-status={ready ? "ready" : status}>
      {/* One row, always the same height: the field (or the brand it found) + one primary action. */}
      <form onSubmit={submit} className="relative" noValidate>
        <label htmlFor="brand-url" className="sr-only">{t("l12.bp.label")}</label>
        <div className={cn("flex items-center gap-2 rounded-[18px] border bg-surface pl-4 pr-2 shadow-float transition-[border-color,box-shadow] duration-200", status === "error" ? "border-warn/60" : "border-hairline focus-within:border-ink/40 focus-within:shadow-[0_0_0_4px_rgba(21,19,15,0.06)]", compact ? "h-[56px]" : "h-[60px] sm:h-[64px]")}>
          <AnimatePresence mode="wait" initial={false}>
            {ready ? (
              <motion.button key="chip" type="button" onClick={() => setEditing(true)} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.3, ease: EASE }} className="flex min-w-0 flex-1 items-center gap-2.5 h-full text-left focus-visible:outline-none group" aria-label={t("l12.bp.cta_again")} data-testid="brand-preview-chip">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-[9px] text-[12px] font-heading font-bold" style={{ background: preview.logo_url ? "transparent" : preview.color, color: onColor(preview.color) }}>
                  {preview.logo_url ? <img src={preview.logo_url} alt="" className="h-full w-full object-contain" referrerPolicy="no-referrer" /> : (preview.name || "?").charAt(0).toUpperCase()}
                </span>
                <span className="min-w-0 flex items-baseline gap-2 truncate">
                  <span className="text-[15px] sm:text-[16px] font-medium text-ink truncate" data-testid="brand-preview-name">{preview.name}</span>
                  <span className="hidden sm:inline font-mono text-[12px] text-ink/45 truncate">{preview.domain}</span>
                </span>
                <Check className="h-4 w-4 shrink-0 text-success" strokeWidth={2.4} />
              </motion.button>
            ) : (
              <motion.div key="field" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.3, ease: EASE }} className="flex min-w-0 flex-1 items-center gap-2 h-full">
                <Globe className="h-[18px] w-[18px] shrink-0 text-ink/40" strokeWidth={1.8} />
                <input
                  id="brand-url"
                  ref={inputRef}
                  type="text"
                  inputMode="url"
                  autoComplete="url"
                  autoCapitalize="none"
                  spellCheck={false}
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder={t("l12.bp.placeholder")}
                  disabled={status === "loading"}
                  className="min-w-0 flex-1 h-full bg-transparent text-[16px] sm:text-[17px] text-ink placeholder:text-ink/35 outline-none disabled:opacity-60"
                  data-testid="brand-url-input"
                />
              </motion.div>
            )}
          </AnimatePresence>
          {ready ? (
            <Link to={`/app/register?brand=${preview.token}`} className="shrink-0"><PrimaryButton type="button" size="md" className={cn("rounded-[13px]", compact ? "h-[42px] px-4" : "h-[46px] sm:h-[48px] px-4 sm:px-5")} data-testid="brand-preview-start"><span className="hidden sm:inline">{t("l12.bp.start_with", { name: preview.name })}</span><span className="sm:hidden">{t("l12.bp.start_short")}</span></PrimaryButton></Link>
          ) : (
            <PrimaryButton type="submit" size="md" loading={status === "loading"} className={cn("shrink-0 rounded-[13px]", compact ? "h-[42px] px-4" : "h-[46px] sm:h-[48px] px-4 sm:px-5")} data-testid="brand-url-submit">
              <span className="hidden sm:inline">{t("l12.bp.cta")}</span>
              <span className="sm:hidden">{t("l12.bp.cta_short")}</span>
            </PrimaryButton>
          )}
        </div>
      </form>

      {/* One quiet line beneath: hint · progress · error · or what we read + the small actions. */}
      <div className="mt-3 min-h-[22px] text-[13px]" aria-live="polite">
        <AnimatePresence mode="wait" initial={false}>
          {status === "loading" ? (
            <motion.p key={`s${step}`} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.3, ease: EASE }} className="flex items-center gap-2 text-ink/60" data-testid="brand-preview-progress">
              <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-lavender opacity-60" /><span className="relative inline-flex h-2 w-2 rounded-full bg-lavender" /></span>
              {t(STEPS[step])}
            </motion.p>
          ) : status === "error" ? (
            <motion.p key="err" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="text-warn" data-testid="brand-preview-error">{error}</motion.p>
          ) : ready ? (
            <motion.div key="ready" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4, ease: EASE }} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-ink/50" data-testid="brand-preview-meta">
              <span className="inline-flex min-w-0 items-center gap-2">
                {preview.hero_image_url ? (
                  <span className="relative h-5 w-7 shrink-0 overflow-hidden rounded-[5px] ring-1 ring-ink/10" aria-hidden><img src={`${BACKEND_URL}${preview.hero_image_url}`} alt="" className="absolute inset-0 h-full w-full object-cover" data-testid="brand-preview-hero-thumb" /></span>
                ) : (
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full ring-2 ring-surface shadow-soft" style={{ background: preview.color }} aria-hidden />
                )}
                <span className="truncate">{t("l12.bp.read_from", { domain: preview.domain })}{partialNote && <span className="text-ink/45" data-testid="brand-preview-partial"> · {partialNote}</span>}</span>
              </span>
              <span aria-hidden className="text-ink/25">·</span>
              <button type="button" onClick={() => { reset(); setEditing(false); setUrl(""); }} className={textBtn} data-testid="brand-preview-reset"><RotateCcw className="h-3 w-3" /> {t("l12.bp.reset")}</button>
              <button type="button" onClick={() => copyText(shareLink, t("l12.bp.link_copied")).catch(() => toast.error(t("l12.bp.link_failed")))} className={textBtn} data-testid="brand-preview-copy"><Link2 className="h-3 w-3" /> {t("l12.bp.copy_link")}</button>
            </motion.div>
          ) : (
            <motion.p key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-ink/50" data-testid="brand-preview-hint">
              <span className="inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-lavender" strokeWidth={2} />{t("l12.bp.hint")}</span>
              {preview && editing && <button type="button" onClick={() => setEditing(false)} className={textBtn} data-testid="brand-preview-keep">{t("l12.bp.keep", { name: preview.name })}</button>}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
